import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

interface AccountCompletionStepProps {
  onComplete: () => void;
}

const AccountCompletionStep: React.FC<AccountCompletionStepProps> = ({ onComplete }) => {
  const { watch } = useFormContext();
  const navigate = useNavigate();
  
  const selectedPlan = watch('plan') as 'starter' | 'growth' | 'professional' | 'business';
  const billingCycle = watch('billingCycle') as 'monthly' | 'yearly';

  const plans: Record<'starter' | 'growth' | 'professional' | 'business', { name: string; monthlyPrice: string; yearlyPrice: string }> = {
    starter: { name: 'Starter + Free Trial', monthlyPrice: '£49', yearlyPrice: '£490' },
    growth: { name: 'Growth', monthlyPrice: '£89', yearlyPrice: '£890' },
    professional: { name: 'Professional', monthlyPrice: '£129', yearlyPrice: '£1290' },
    business: { name: 'Business', monthlyPrice: '£299', yearlyPrice: '£2990' }
  };

  const isFreeTrial = selectedPlan === 'starter';

  // Auto-redirect to dashboard after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleGoToDashboard();
    }, 3000);

    return () => clearTimeout(timer);
  }, [navigate, onComplete]);

  const handleGoToDashboard = () => {
    // Call the onComplete callback first to clean up any signup state
    onComplete();
    // Then navigate to dashboard
    navigate('/dashboard');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center space-y-4 max-w-md mx-auto"
    >
      {/* Success Icon */}
      <div className="flex justify-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"
        >
          <CheckCircleIcon className="w-7 h-7 text-green-600" />
        </motion.div>
      </div>

      {/* Success Message */}
      <div className="space-y-1">
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-xl font-bold text-gray-900"
        >
          Welcome to TrendBeat!
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-sm text-gray-600"
        >
          Your account has been successfully created and activated.
        </motion.p>
      </div>

      {/* Plan Summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
      >
        <h3 className="text-base font-semibold text-gray-900 mb-2">Your Plan</h3>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Plan:</span>
            <span className="font-medium text-gray-900">{plans[selectedPlan].name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Billing:</span>
            <span className="font-medium text-gray-900">
              {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Amount:</span>
            <span className="font-medium text-gray-900">
              {plans[selectedPlan][billingCycle === 'monthly' ? 'monthlyPrice' : 'yearlyPrice']}
              /{billingCycle === 'monthly' ? 'month' : 'year'}
            </span>
          </div>
          {isFreeTrial && (
            <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
              <p className="text-xs text-purple-700 font-medium">
                🎉 7-day free trial starts now!
              </p>
              <p className="text-xs text-purple-600 mt-0.5">
                First payment in 7 days
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Next Steps - Compact */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="space-y-2"
      >
        <h3 className="text-base font-semibold text-gray-900">What's Next?</h3>
        <div className="space-y-1 text-xs text-gray-600">
          <p>• Complete your profile setup</p>
          <p>• Explore the dashboard and features</p>
          <p>• Start tracking trends and insights</p>
        </div>
      </motion.div>

      {/* Redirect Notice */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="text-xs text-gray-500"
      >
        Redirecting to your dashboard in a few seconds...
      </motion.div>

      {/* Manual Continue Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="pt-2"
      >
        <button
          onClick={handleGoToDashboard}
          className="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors text-sm"
        >
          Go to Dashboard
        </button>
      </motion.div>
    </motion.div>
  );
};

export default AccountCompletionStep;
